import { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
// emailService eliminado - La DB se encarga
import { Loader2, MapPin, AlertCircle, CheckCircle2, User, Mail, Phone, ShieldCheck, KeyRound, X, ChevronDown, Euro, Ruler, DoorOpen, Lock, Box, CalendarClock, Info, ArrowLeft, Image as ImageIcon } from 'lucide-react';
import { MAJOR_CITIES, SPANISH_PROVINCES, findProvince } from '@/lib/locations';
import { ListingImageUploader } from '@/components/listings/ListingImageUploader';

// Función auxiliar para formatear texto (Title Case)
const formatText = (text: string) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export function PublishPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(!!editId);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);

  const [citySuggestions, setCitySuggestions] = useState<typeof MAJOR_CITIES>([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const cityInputRef = useRef<HTMLDivElement>(null);

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [existingImages, setExistingImages] = useState<string[]>([]);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    operation_type: 'rent' as 'rent' | 'sale',
    city: '',
    province: '',
    address: '',
    area_m2: '',
    height_m: '',
    volume_m3: '',
    expenses_included: false,
    community_fee: '',
    ibi: '',
    available_now: true,
    available_from: new Date().toISOString().split('T')[0],
    min_months: '1',
    features: {
      access_street: false,
      access_basement: false,
      access_elevator: false,
      access_freight_elevator: false,
      access_garage: false,
      access_wide_corridor: false,
      access_metal_door: false,
      access_private_door: false,
      feat_lock: false,
      feat_security: false,
      feat_light: false,
      feat_ventilation: false,
      feat_power: false,
      feat_24h: false,
      feat_individual: true,
      feat_shared: false,
      use_personal: false,
      use_bicycle: false,
      use_documents: false,
      use_stock: false,
      use_office: false,
      use_moving: false
    }
  });

  const [authData, setAuthData] = useState({
    email: '',
    fullName: '',
    phone: ''
  });

  useEffect(() => {
    async function loadListingData() {
        if (!editId || !user) return;
        
        try {
            const { data, error } = await supabase
                .from('listings')
                .select('*')
                .eq('id', editId)
                .eq('user_id', user.id)
                .single();

            if (error) throw error;
            if (data) {
                setFormData({
                    title: data.title,
                    description: data.description || '',
                    price: data.price.toString(),
                    operation_type: data.operation_type,
                    city: data.city,
                    province: data.province || '',
                    address: data.address || '',
                    area_m2: data.area_m2.toString(),
                    height_m: data.height_m?.toString() || '',
                    volume_m3: data.volume_m3?.toString() || '',
                    expenses_included: data.expenses_included || false,
                    community_fee: data.community_fee?.toString() || '',
                    ibi: data.ibi?.toString() || '',
                    available_now: !data.available_from || new Date(data.available_from) <= new Date(),
                    available_from: data.available_from ? data.available_from.split('T')[0] : new Date().toISOString().split('T')[0],
                    min_months: data.min_months?.toString() || '1',
                    features: { ...formData.features, ...(data.features || {}) }
                });
                setExistingImages(data.images || []);
            }
        } catch (err) {
            console.error(err);
            setError('No se pudo cargar el anuncio o no tienes permiso para editarlo.');
            navigate('/profile');
        } finally {
            setFetching(false);
        }
    }

    if (editId) {
        loadListingData();
    }
  }, [editId, user, navigate]);

  useEffect(() => {
    const area = parseFloat(formData.area_m2);
    const height = parseFloat(formData.height_m);
    
    if (!isNaN(area) && !isNaN(height) && area > 0 && height > 0) {
        const vol = (area * height).toFixed(2);
        setFormData(prev => ({ ...prev, volume_m3: vol }));
    }
  }, [formData.area_m2, formData.height_m]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (cityInputRef.current && !cityInputRef.current.contains(event.target as Node)) {
        setShowCitySuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInitialSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.city.trim() || !formData.province.trim()) {
      setError('Por favor indica la ciudad y provincia.');
      return;
    }

    if (selectedFiles.length === 0 && existingImages.length === 0) {
        setError('Por favor sube al menos una imagen de tu trastero.');
        return;
    }

    if (user) {
      await publishListing(user.id, user.email!, user.user_metadata?.full_name, user.user_metadata?.phone, formData);
    } else {
      if (!authData.email || !authData.fullName) {
        setError('Por favor completa tu nombre y email.');
        return;
      }
      
      setLoading(true);
      try {
        const { error } = await supabase.auth.signInWithOtp({
          email: authData.email,
          options: { 
            shouldCreateUser: true,
            data: {
              full_name: authData.fullName,
              phone: authData.phone
            }
          }
        });

        if (error) throw error;
        setShowOtpModal(true);
      } catch (err: any) {
        setError(err.message || 'Error al enviar el código de verificación.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleVerifyOtpAndPublish = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpLoading(true);
    setError(null);

    try {
      const { data, error: verifyError } = await supabase.auth.verifyOtp({
        email: authData.email,
        token: otp,
        type: 'magiclink'
      });

      if (verifyError) throw verifyError;
      if (!data.user) throw new Error('No se pudo verificar el usuario.');

      await publishListing(
        data.user.id, 
        authData.email, 
        authData.fullName, 
        authData.phone, 
        formData
      );
      
      setShowOtpModal(false);

    } catch (err: any) {
      console.error(err);
      setError('Código incorrecto o expirado. Inténtalo de nuevo.');
    } finally {
      setOtpLoading(false);
    }
  };

  const compressImage = async (file: File): Promise<File> => {
    return new Promise((resolve) => {
      if (!file.type.startsWith('image/')) {
        resolve(file);
        return;
      }

      const img = new Image();
      img.src = URL.createObjectURL(file);
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
            resolve(file); 
            return;
        }

        const MAX_WIDTH = 1920;
        const MAX_HEIGHT = 1080;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_WIDTH) {
            height *= MAX_WIDTH / width;
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width *= MAX_HEIGHT / height;
            height = MAX_HEIGHT;
          }
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob((blob) => {
          if (!blob) {
            resolve(file);
            return;
          }
          const newName = file.name.replace(/\.[^/.]+$/, "") + ".webp";
          const compressedFile = new File([blob], newName, {
            type: 'image/webp',
            lastModified: Date.now(),
          });
          resolve(compressedFile);
        }, 'image/webp', 0.8);
      };

      img.onerror = () => resolve(file);
    });
  };

  const uploadImages = async (files: File[]): Promise<string[]> => {
    const uploadedUrls: string[] = [];

    for (const file of files) {
        try {
            const compressedFile = await compressImage(file);
            const fileExt = compressedFile.name.split('.').pop();
            const fileName = `${Math.random().toString(36).substring(2)}_${Date.now()}.${fileExt}`;
            const filePath = `${fileName}`;

            const { error: uploadError } = await supabase.storage
                .from('listing-images')
                .upload(filePath, compressedFile);

            if (uploadError) throw uploadError;

            const { data } = supabase.storage.from('listing-images').getPublicUrl(filePath);
            uploadedUrls.push(data.publicUrl);
        } catch (error) {
            console.error('Error uploading file:', file.name, error);
        }
    }
    return uploadedUrls;
  };

  const publishListing = async (userId: string, email: string, name?: string, phone?: string, dataToPublish = formData) => {
    setLoading(true);
    try {
      const newImageUrls = await uploadImages(selectedFiles);
      const finalImages = [...existingImages, ...newImageUrls];

      if (finalImages.length === 0) {
          throw new Error('No se ha podido subir ninguna imagen. Inténtalo de nuevo.');
      }

      const formattedCity = formatText(dataToPublish.city.trim());
      const formattedProvince = formatText(dataToPublish.province.trim());
      const formattedTitle = dataToPublish.title.trim();

      const payload = {
        user_id: userId,
        title: formattedTitle,
        description: dataToPublish.description,
        price: Number(dataToPublish.price),
        operation_type: dataToPublish.operation_type,
        area_m2: Number(dataToPublish.area_m2),
        height_m: dataToPublish.height_m ? Number(dataToPublish.height_m) : null,
        volume_m3: dataToPublish.volume_m3 ? Number(dataToPublish.volume_m3) : null,
        expenses_included: dataToPublish.expenses_included,
        community_fee: dataToPublish.community_fee ? Number(dataToPublish.community_fee) : 0,
        ibi: dataToPublish.ibi ? Number(dataToPublish.ibi) : 0,
        available_from: dataToPublish.available_now ? new Date().toISOString() : dataToPublish.available_from,
        min_months: Number(dataToPublish.min_months),
        city: formattedCity,
        province: formattedProvince,
        address: dataToPublish.address,
        images: finalImages,
        features: dataToPublish.features,
        is_active: true,
        status: editId ? 'approved' : 'pending', 
        contact_name: name || authData.fullName,
        contact_email: email,
        contact_phone: phone || authData.phone
      };

      if (editId) {
        const { error: updateError } = await supabase
            .from('listings')
            .update(payload)
            .eq('id', editId)
            .eq('user_id', userId);
        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
            .from('listings')
            .insert(payload);
        if (insertError) throw insertError;
      }

      // La base de datos detectará la inserción/actualización y enviará el email automáticamente.
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Error al guardar el anuncio.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
        const checked = (e.target as HTMLInputElement).checked;
        if (name === 'expenses_included' || name === 'available_now') {
            setFormData(prev => ({ ...prev, [name]: checked }));
        } else {
            setFormData(prev => ({
                ...prev,
                features: {
                    ...prev.features,
                    [name]: checked
                }
            }));
        }
    } else {
        setFormData(prev => ({ ...prev, [name]: value }));
    }

    if (name === 'city') {
      if (value.length > 1) {
        const filtered = MAJOR_CITIES.filter(c => 
          c.name.toLowerCase().includes(value.toLowerCase())
        ).slice(0, 5);
        setCitySuggestions(filtered);
        setShowCitySuggestions(true);
      } else {
        setShowCitySuggestions(false);
      }
    }
  };

  const handleCitySelect = (city: typeof MAJOR_CITIES[0]) => {
    setFormData(prev => ({
      ...prev,
      city: city.name,
      province: city.province
    }));
    setShowCitySuggestions(false);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'city' || name === 'province') {
        setTimeout(() => {
            const formatted = formatText(value);
            if (formatted !== value) {
                setFormData(prev => ({ ...prev, [name]: formatted }));
            }
            if (name === 'city' && !formData.province) {
                const prov = findProvince(value);
                if (prov) {
                    setFormData(prev => ({ ...prev, province: prov }));
                }
            }
        }, 200);
    }
  };

  const handleAuthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAuthData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  if (fetching) {
      return (
        <div className="min-h-screen flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      );
  }

  if (success) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center px-4 text-center max-w-2xl mx-auto">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6 animate-in zoom-in duration-300">
          <CheckCircle2 className="w-10 h-10 text-green-600" />
        </div>
        <h2 className="text-3xl font-bold text-slate-900 mb-4">
            {editId ? '¡Cambios Guardados!' : '¡Anuncio Recibido!'}
        </h2>
        <p className="text-lg text-slate-600 mb-4">
          {editId 
            ? 'Tu anuncio ha sido actualizado correctamente.'
            : 'Tu anuncio ha sido enviado y está pendiente de aprobación.'}
        </p>
        
        <div className="flex gap-4 justify-center">
          <button 
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-slate-100 text-slate-700 font-medium rounded-xl hover:bg-slate-200 transition-colors"
          >
            Volver al inicio
          </button>
          <button 
            onClick={() => navigate('/profile')}
            className="px-6 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 transition-colors"
          >
            Ir a mis anuncios
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-12 relative">
      {editId && (
          <button 
            onClick={() => navigate('/profile')}
            className="mb-6 flex items-center text-slate-500 hover:text-blue-600 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Cancelar edición
          </button>
      )}

      <div className="mb-8 text-center md:text-left">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">
            {editId ? 'Editar Anuncio' : 'Publicar Anuncio'}
        </h1>
        <p className="text-slate-600">
          {editId 
            ? 'Modifica los datos de tu trastero.' 
            : 'Completa todos los detalles posibles para encontrar inquilino más rápido.'}
        </p>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-xl mb-6 flex items-center gap-2 border border-red-100">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleInitialSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-slate-200 space-y-6">
            <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600" />
              1. Tipo y Ubicación
            </h3>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-3">¿Qué quieres hacer?</label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, operation_type: 'rent' }))}
                  className={`py-3 px-4 rounded-xl border-2 font-medium transition-all ${
                    formData.operation_type === 'rent' 
                      ? 'border-blue-600 bg-blue-50 text-blue-700' 
                      : 'border-slate-200 hover:border-slate-300 text-slate-600'
                  }`}
                >
                  Alquilarlo
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, operation_type: 'sale' }))}
                  className={`py-3 px-4 rounded-xl border-2 font-medium transition-all ${
                    formData.operation_type === 'sale' 
                      ? 'border-blue-600 bg-blue-50 text-blue-700' 
                      : 'border-slate-200 hover:border-slate-300 text-slate-600'
                  }`}
                >
                  Venderlo
                </button>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Título del Anuncio</label>
                <input
                  type="text"
                  name="title"
                  required
                  placeholder="Ej: Trastero amplio en centro de Madrid"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  value={formData.title}
                  onChange={handleChange}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="relative" ref={cityInputRef}>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Ciudad</label>
                  <input
                    type="text"
                    name="city"
                    required
                    autoComplete="off"
                    placeholder="Ej: Madrid"
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    value={formData.city}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {showCitySuggestions && citySuggestions.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                      {citySuggestions.map((city) => (
                        <button
                          key={`${city.name}-${city.province}`}
                          type="button"
                          className="w-full text-left px-4 py-2 hover:bg-blue-50 text-sm text-slate-700 flex justify-between items-center"
                          onClick={() => handleCitySelect(city)}
                        >
                          <span className="font-medium">{city.name}</span>
                          <span className="text-xs text-slate-400">{city.province}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Provincia</label>
                  <div className="relative">
                    <select
                        name="province"
                        required
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none appearance-none bg-white"
                        value={formData.province}
                        onChange={handleChange}
                    >
                        <option value="">Selecciona...</option>
                        {SPANISH_PROVINCES.map(prov => (
                            <option key={prov} value={prov}>{prov}</option>
                        ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4 pointer-events-none" />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Dirección Exacta</label>
                <input
                  type="text"
                  name="address"
                  placeholder="Calle, número..."
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  value={formData.address}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>
          <div className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-slate-200 space-y-6">
            <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <Ruler className="w-5 h-5 text-blue-600" />
              2. Dimensiones
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Superficie (m²)</label>
                  <div className="relative">
                    <input
                        type="number"
                        name="area_m2"
                        required
                        min="0"
                        step="0.1"
                        placeholder="0"
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                        value={formData.area_m2}
                        onChange={handleChange}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">m²</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Altura (m) <span className="text-slate-400 font-normal">(Opcional)</span></label>
                  <div className="relative">
                    <input
                        type="number"
                        name="height_m"
                        min="0"
                        step="0.1"
                        placeholder="2.5"
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                        value={formData.height_m}
                        onChange={handleChange}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">m</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Volumen (m³)</label>
                  <div className="relative">
                    <input
                        type="number"
                        name="volume_m3"
                        readOnly
                        placeholder="Auto"
                        className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-slate-500 cursor-not-allowed"
                        value={formData.volume_m3}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">m³</span>
                  </div>
                </div>
            </div>
          </div>
          <div className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-slate-200 space-y-6">
            <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <Euro className="w-5 h-5 text-blue-600" />
              3. Precio y Gastos
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {formData.operation_type === 'rent' ? 'Precio Mensual (€)' : 'Precio de Venta (€)'}
                  </label>
                  <input
                    type="number"
                    name="price"
                    required
                    min="0"
                    placeholder="0"
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-lg font-semibold"
                    value={formData.price}
                    onChange={handleChange}
                  />
                </div>
                {formData.operation_type === 'rent' && (
                    <div className="flex items-center h-full pt-6">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input 
                                type="checkbox" 
                                name="expenses_included"
                                checked={formData.expenses_included}
                                onChange={handleChange}
                                className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500 border-gray-300"
                            />
                            <span className="text-slate-700 font-medium">Gastos incluidos en el precio</span>
                        </label>
                    </div>
                )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-slate-100">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Comunidad Mensual (€)</label>
                  <input
                    type="number"
                    name="community_fee"
                    min="0"
                    placeholder="Opcional"
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    value={formData.community_fee}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">IBI Anual (€)</label>
                  <input
                    type="number"
                    name="ibi"
                    min="0"
                    placeholder="Opcional"
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    value={formData.ibi}
                    onChange={handleChange}
                  />
                </div>
            </div>
          </div>
          <div className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-slate-200 space-y-8">
            <div>
                <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2 mb-4">
                    <DoorOpen className="w-5 h-5 text-blue-600" />
                    4. Acceso y Ubicación
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                        { key: 'access_street', label: 'A pie de calle' },
                        { key: 'access_basement', label: 'En sótano' },
                        { key: 'access_elevator', label: 'Acceso con ascensor' },
                        { key: 'access_freight_elevator', label: 'Acceso con montacargas' },
                        { key: 'access_garage', label: 'Acceso directo desde garaje' },
                        { key: 'access_wide_corridor', label: 'Pasillo ancho' },
                        { key: 'access_metal_door', label: 'Puerta metálica' },
                        { key: 'access_private_door', label: 'Puerta privada' },
                    ].map((item) => (
                        <label key={item.key} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-2 rounded-lg transition-colors">
                            <input 
                                type="checkbox" 
                                name={item.key}
                                checked={formData.features[item.key as keyof typeof formData.features]}
                                onChange={handleChange}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 border-gray-300"
                            />
                            <span className="text-slate-700 text-sm">{item.label}</span>
                        </label>
                    ))}
                </div>
            </div>
            <div className="border-t border-slate-100 my-4"></div>
            <div>
                <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2 mb-4">
                    <Lock className="w-5 h-5 text-blue-600" />
                    5. Características del Trastero
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                        { key: 'feat_lock', label: 'Cerradura propia' },
                        { key: 'feat_security', label: 'Vigilancia / Seguridad' },
                        { key: 'feat_light', label: 'Iluminación' },
                        { key: 'feat_ventilation', label: 'Ventilación' },
                        { key: 'feat_power', label: 'Toma eléctrica' },
                        { key: 'feat_24h', label: 'Acceso 24h' },
                        { key: 'feat_individual', label: 'Trastero individual' },
                        { key: 'feat_shared', label: 'Trastero compartido' },
                    ].map((item) => (
                        <label key={item.key} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-2 rounded-lg transition-colors">
                            <input 
                                type="checkbox" 
                                name={item.key}
                                checked={formData.features[item.key as keyof typeof formData.features]}
                                onChange={handleChange}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 border-gray-300"
                            />
                            <span className="text-slate-700 text-sm">{item.label}</span>
                        </label>
                    ))}
                </div>
            </div>
            <div className="border-t border-slate-100 my-4"></div>
            <div>
                <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2 mb-4">
                    <Box className="w-5 h-5 text-blue-600" />
                    6. Usos Permitidos
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                    {[
                        { key: 'use_personal', label: 'Uso personal' },
                        { key: 'use_bicycle', label: 'Bicicletas' },
                        { key: 'use_documents', label: 'Documentos' },
                        { key: 'use_stock', label: 'Stock de pequeño negocio' },
                        { key: 'use_office', label: 'Material de oficina' },
                        { key: 'use_moving', label: 'Mudanzas temporales' },
                    ].map((item) => (
                        <label key={item.key} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-2 rounded-lg transition-colors">
                            <input 
                                type="checkbox" 
                                name={item.key}
                                checked={formData.features[item.key as keyof typeof formData.features]}
                                onChange={handleChange}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 border-gray-300"
                            />
                            <span className="text-slate-700 text-sm">{item.label}</span>
                        </label>
                    ))}
                </div>
                <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg flex items-center gap-2 border border-red-100">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span><strong>Prohibido:</strong> No se permite almacenar materiales peligrosos, inflamables o ilegales.</span>
                </div>
            </div>
          </div>
          <div className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-slate-200 space-y-6">
            <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-blue-600" />
              7. Disponibilidad
            </h3>
            <div className="space-y-4">
                <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                        type="checkbox" 
                        name="available_now"
                        checked={formData.available_now}
                        onChange={handleChange}
                        className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500 border-gray-300"
                    />
                    <span className="text-slate-700 font-medium">Disponible ahora mismo</span>
                </label>
                {!formData.available_now && (
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Fecha de disponibilidad</label>
                        <input
                            type="date"
                            name="available_from"
                            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                            value={formData.available_from}
                            onChange={handleChange}
                        />
                    </div>
                )}
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Duración mínima (meses)</label>
                    <input
                        type="number"
                        name="min_months"
                        min="1"
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                        value={formData.min_months}
                        onChange={handleChange}
                    />
                </div>
            </div>
          </div>
          <div className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-slate-200 space-y-6">
            <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <Info className="w-5 h-5 text-blue-600" />
              8. Descripción y Fotos
            </h3>
            <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Descripción</label>
                <textarea
                  name="description"
                  rows={4}
                  placeholder="Describe tu trastero: ¿Es seco? ¿Tiene fácil acceso para descargar?..."
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                  value={formData.description}
                  onChange={handleChange}
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                    <ImageIcon className="w-4 h-4 text-blue-600" />
                    Fotos del Trastero
                </label>
                <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800 mb-3 border border-blue-100 flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>
                        <strong>Consejo:</strong> Sube fotos horizontales y con buena luz. 
                        La primera foto será la portada de tu anuncio.
                    </span>
                </div>
                <ListingImageUploader 
                    existingImages={existingImages}
                    onImagesChange={(files, kept) => {
                        setSelectedFiles(files);
                        setExistingImages(kept);
                    }}
                />
            </div>
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 sticky top-24">
            <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              Tus Datos
            </h3>
            {user ? (
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-blue-200 rounded-full flex items-center justify-center text-blue-700 font-bold">
                    {user.email?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Publicando como:</p>
                    <p className="text-sm text-slate-600">{user.email}</p>
                  </div>
                </div>
                <p className="text-xs text-blue-600 mt-2">
                  Tu anuncio quedará vinculado a esta cuenta.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-yellow-50 p-3 rounded-lg text-sm text-yellow-800 border border-yellow-100 mb-4 flex gap-2">
                  <ShieldCheck className="w-5 h-5 shrink-0" />
                  <span>Te enviaremos un código a tu email para verificar tu identidad al publicar.</span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Nombre Completo</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 h-4 w-4" />
                    <input
                      type="text"
                      name="fullName"
                      required={!user}
                      className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      value={authData.fullName}
                      onChange={handleAuthChange}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 h-4 w-4" />
                    <input
                      type="email"
                      name="email"
                      required={!user}
                      className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      value={authData.email}
                      onChange={handleAuthChange}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Teléfono</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 h-4 w-4" />
                    <input
                      type="tel"
                      name="phone"
                      required={!user}
                      className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      value={authData.phone}
                      onChange={handleAuthChange}
                    />
                  </div>
                </div>
              </div>
            )}
            <div className="mt-8 pt-6 border-t border-slate-100">
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3.5 rounded-xl transition-all shadow-lg shadow-blue-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Procesando...
                  </>
                ) : (
                  editId ? 'Guardar Cambios' : 'Publicar Anuncio'
                )}
              </button>
              <p className="text-xs text-slate-500 text-center mt-3">
                Al publicar aceptas nuestros términos y condiciones.
              </p>
            </div>
          </div>
        </div>
      </form>

      {showOtpModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 animate-in zoom-in-95 duration-200 relative">
            <button 
              onClick={() => setShowOtpModal(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <KeyRound className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900">Verificar Email</h3>
              <p className="text-slate-500 mt-2">
                Introduce el código que hemos enviado a <br />
                <span className="font-medium text-slate-900">{authData.email}</span>
              </p>
            </div>
            <form onSubmit={handleVerifyOtpAndPublish} className="space-y-6">
              <div>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  placeholder="Código"
                  className="w-full px-4 py-4 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-center text-3xl tracking-widest font-mono"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  maxLength={8}
                />
              </div>
              <button
                type="submit"
                disabled={otpLoading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3.5 rounded-xl font-bold transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
              >
                {otpLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Verificar y Publicar'
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
